import React, { useState, useEffect } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import Select from 'react-select';
import { DetailedProduct, CatalogueConfig } from '../../../../api/shop/types/productTypes';
import DetailView from '../../../../Common/DetailView';
import { StatusBadge, ProductBadge } from '../../../../Common/BadgeStyles';
import { useCatalogueConfig } from '../../../../contexts/CatalogueConfigContext';
import '../../../../Common/BadgeStyles.css';

interface ProductBasicInfoProps {
  data: Partial<DetailedProduct>;
  onChange: (updates: Partial<DetailedProduct>) => void;
  onFieldTouch?: (fieldName: string) => void;
  isReadOnly?: boolean;
  showValidation?: boolean;
  touchedFields?: string[];
  isSportsNutrition?: boolean;
}

const ProductBasicInfo: React.FC<ProductBasicInfoProps> = ({
  data,
  onChange,
  onFieldTouch,
  isReadOnly = false,
  showValidation = false,
  touchedFields = [],
  isSportsNutrition = false,
}) => {
  // Use shared config from context instead of individual API call
  const { config, loading } = useCatalogueConfig();

  // Convert config data to select options
  const sportOptions = config?.sports.map(sport => ({
    value: sport.value,
    label: sport.label
  })) || [];

  const categoryOptions = config ? [
    ...config.categories.apparels.map(cat => ({ value: cat.value, label: cat.label })),
    ...config.categories.accessories.map(cat => ({ value: cat.value, label: cat.label })),
    ...(config.categories.sportsNutrition || []).map(cat => ({ value: cat.value, label: cat.label }))
  ] : [];

  const genderOptions = config?.genders.map(gender => ({
    value: gender.value,
    label: gender.label
  })) || [];

  const badgeOptions = config?.badges.map(badge => ({
    value: badge.value,
    label: badge.label
  })) || [];

  const handleInputChange = (field: string, value: any) => {
    if (isReadOnly) return;
    
    // Mark field as touched
    onFieldTouch?.(field);
    
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      onChange({
        [parent]: {
          ...(data[parent as keyof DetailedProduct] as any),
          [child]: value,
        },
      });
    } else {
      onChange({ [field]: value });
    }
  };

  // Check if field should show validation error
  const shouldShowValidation = (fieldName: string): boolean => {
    if (isReadOnly) return false;
    return showValidation || touchedFields.includes(fieldName);
  };

  // Check if field is invalid
  const isFieldInvalid = (fieldName: string, fieldValue: any): boolean => {
    if (!shouldShowValidation(fieldName)) return false;
    
    // Fields that are not required
    if (fieldName === 'additionalInfo.material' || fieldName === 'sport') {
      return false;
    }
    
    // For pricing fields, 0 is invalid
    if (fieldName.startsWith('pricing.')) {
      return !fieldValue || fieldValue === '' || fieldValue === 0;
    }
    
    // For other fields, empty or null is invalid
    return !fieldValue || fieldValue === '';
  };

  // Get field validation class
  const getFieldClass = (fieldName: string, fieldValue: any): string => {
    const baseClass = 'product-detail-form-control';
    if (isFieldInvalid(fieldName, fieldValue)) {
      return `${baseClass} is-invalid`;
    }
    return baseClass;
  };

  // If in read-only mode, show DetailView layout
  if (isReadOnly) {
    const detailFields = [
      { label: 'Product Name', value: data.name || 'N/A' },
      ...(!isSportsNutrition ? [{ label: 'Sport', value: data.sport || 'N/A' }] : []),
      { label: 'Category', value: data.category || 'N/A' },
      { label: 'Product MRP', value: data.pricing?.mrp ? `₹${data.pricing.mrp}` : '₹0' },
      { label: 'Selling Price', value: data.pricing?.sellingPrice ? `₹${data.pricing.sellingPrice}` : '₹0' },
      { 
        label: 'Product Badges', 
        value: data.badges && data.badges.length > 0 
          ? (
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {data.badges.map((badge, index) => {
                  const configLabel = badgeOptions.find(opt => opt.value === badge)?.label;
                  return <ProductBadge key={index} badge={badge} label={configLabel} />;
                })}
              </div>
            )
          : 'No badges selected',
        isBadge: true
      },
      { label: 'Gender', value: data.gender || 'UNISEX' },
      { label: 'Estimated Pickup Days', value: `${data.estimatedPickupDays || 5} days` },
      { 
        label: 'Status', 
        value: <StatusBadge status={data.status as 'ACTIVE' | 'INACTIVE' || 'ACTIVE'} />,
        isBadge: true
      },
      { 
        label: 'Product Description', 
        value: data.description || 'No description provided',
        span: 1
      },
      { 
        label: 'Material / Ingredients', 
        value: data.additionalInfo?.material || 'No material information provided',
        span: 1
      }
    ];

    if (data.additionalInfo?.productDesc) {
      detailFields.push({ 
        label: 'Additional Product Description', 
        value: data.additionalInfo.productDesc,
        span: 1
      });
    }

    return (
      <DetailView
        title="Basic Information"
        fields={detailFields}
        layout="grid"
        columns={3}
        noWrapper={true}
      />
    );
  }

  // Show loading state while config is loading
  if (loading && !isReadOnly) {
    return (
      <div>
        <h4 className="product-detail-section-title mb-4">Basic Information</h4>
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading configuration...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h4 className="product-detail-section-title mb-4">Basic Information</h4>
      
      <Row>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label className="product-detail-form-label">
              Product Name<span className="required-asterisk">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={data.name || ''}
              onChange={(e) => handleInputChange('name', e.target.value)}
              onBlur={() => onFieldTouch?.('name')}
              placeholder="Enter product name"
              className={getFieldClass('name', data.name)}
              disabled={isReadOnly}
            />
          </Form.Group>
        </Col>

        {!isSportsNutrition && <Col md={3} className="mb-3">
          <Form.Group>
            <Form.Label className="product-detail-form-label">
              Sport
            </Form.Label>
            <Select
              value={sportOptions.find(opt => opt.value === data.sport)}
              onChange={(option: any) => handleInputChange('sport', option?.value || '')}
              options={sportOptions}
              placeholder="Select sport"
              isDisabled={isReadOnly}
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                control: (base: any, state: any) => ({
                  ...base,
                  border: '1px solid #e3e6f0',
                  borderRadius: '6px',
                  minHeight: '42px',
                  '&:hover': {
                    borderColor: '#e3e6f0'
                  }
                })
              }}
            />
          </Form.Group>
        </Col>}

        <Col md={3} className="mb-3">
          <Form.Group>
            <Form.Label className="product-detail-form-label">
              Category<span className="required-asterisk">*</span>
            </Form.Label>
            <Select
              value={categoryOptions.find(opt => opt.value === data.category)}
              onChange={(option: any) => handleInputChange('category', option?.value || '')}
              options={categoryOptions}
              placeholder="Select category"
              isDisabled={isReadOnly}
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                control: (base: any, state: any) => ({
                  ...base,
                  border: '1px solid #e3e6f0',
                  borderRadius: '6px',
                  minHeight: '42px',
                  '&:hover': {
                    borderColor: '#e3e6f0'
                  }
                })
              }}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label className="product-detail-form-label">
              Product MRP<span className="required-asterisk">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              value={data.pricing?.mrp || ''}
              onChange={(e) => handleInputChange('pricing.mrp', parseFloat(e.target.value) || 0)}
              onBlur={() => onFieldTouch?.('pricing.mrp')}
              placeholder="₹0"
              className={getFieldClass('pricing.mrp', data.pricing?.mrp)}
              disabled={isReadOnly}
            />
          </Form.Group>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label className="product-detail-form-label">
              Selling Price<span className="required-asterisk">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              value={data.pricing?.sellingPrice || ''}
              onChange={(e) => handleInputChange('pricing.sellingPrice', parseFloat(e.target.value) || 0)}
              onBlur={() => onFieldTouch?.('pricing.sellingPrice')}
              placeholder="₹0"
              className={getFieldClass('pricing.sellingPrice', data.pricing?.sellingPrice)}
              disabled={isReadOnly}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label className="product-detail-form-label">
              Product Description<span className="required-asterisk">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={data.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              onBlur={() => onFieldTouch?.('description')}
              placeholder="Enter product description with specifications, features, etc."
              className={`${getFieldClass('description', data.description)} product-detail-textarea`}
              disabled={isReadOnly}
            />
          </Form.Group>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label className="product-detail-form-label">
              Material / Ingredients
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={data.additionalInfo?.material || ''}
              onChange={(e) => handleInputChange('additionalInfo.material', e.target.value)}
              onBlur={() => onFieldTouch?.('additionalInfo.material')}
              placeholder="e.g., Upper of: 35.0% Polyurethane, 65.0% Polyester#Outer sole of: 100.0% Synthetic Rubber, Care Instructions: Machine Washable"
              className={`${getFieldClass('additionalInfo.material', data.additionalInfo?.material)} product-detail-textarea`}
              disabled={isReadOnly}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={4} className="mb-3">
          <Form.Group>
            <Form.Label className="product-detail-form-label">Gender</Form.Label>
            <Select
              value={genderOptions.find(opt => opt.value === data.gender)}
              onChange={(option: any) => handleInputChange('gender', option?.value || 'UNISEX')}
              options={genderOptions}
              placeholder="Select gender"
              isDisabled={isReadOnly}
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                control: (base: any, state: any) => ({
                  ...base,
                  border: '1px solid #e3e6f0',
                  borderRadius: '6px',
                  minHeight: '42px',
                  '&:hover': {
                    borderColor: '#e3e6f0'
                  }
                })
              }}
            />
          </Form.Group>
        </Col>

        <Col md={4} className="mb-3">
          <Form.Group>
            <Form.Label className="product-detail-form-label">Estimated Pickup Days</Form.Label>
            <Select
              value={{ value: data.estimatedPickupDays || 5, label: `${data.estimatedPickupDays || 5} days` }}
              onChange={(option: any) => handleInputChange('estimatedPickupDays', option?.value || 5)}
              options={[
                { value: 1, label: '1 day' },
                { value: 2, label: '2 days' },
                { value: 3, label: '3 days' },
                { value: 5, label: '5 days' },
                { value: 7, label: '7 days' },
                { value: 10, label: '10 days' },
              ]}
              isDisabled={isReadOnly}
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                control: (base: any, state: any) => ({
                  ...base,
                  border: '1px solid #e3e6f0',
                  borderRadius: '6px',
                  minHeight: '42px',
                  '&:hover': {
                    borderColor: '#e3e6f0'
                  }
                })
              }}
            />
          </Form.Group>
        </Col>

        <Col md={4} className="mb-3">
          <Form.Group>
            <Form.Label className="product-detail-form-label">Status</Form.Label>
            <Select
              value={{ value: data.status || 'ACTIVE', label: data.status === 'ACTIVE' ? 'Active' : data.status === 'INACTIVE' ? 'Inactive' : 'Draft' }}
              onChange={(option: any) => handleInputChange('status', option?.value || 'ACTIVE')}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
                { value: 'DRAFT', label: 'Draft' },
              ]}
              isDisabled={isReadOnly}
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                control: (base: any, state: any) => ({
                  ...base,
                  border: '1px solid #e3e6f0',
                  borderRadius: '6px',
                  minHeight: '42px',
                  '&:hover': {
                    borderColor: '#e3e6f0'
                  }
                })
              }}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Product Badges */}
      <Row>
        <Col md={12} className="mb-4">
          <Form.Group>
            <Form.Label className="product-detail-form-label">Product Badges</Form.Label>
            <div className="product-detail-badge-group mt-2">
              {badgeOptions.map(badge => (
                <div
                  key={badge.value}
                  className={`product-detail-badge ${
                    data.badges?.includes(badge.value as any) ? 'product-detail-badge-selected' : 'product-detail-badge-default'
                  }`}
                  onClick={() => {
                    if (isReadOnly) return;
                    const currentBadges = data.badges || [];
                    const updatedBadges = currentBadges.includes(badge.value as any)
                      ? currentBadges.filter(b => b !== badge.value)
                      : [...currentBadges, badge.value as any];
                    handleInputChange('badges', updatedBadges);
                  }}
                  style={{ cursor: isReadOnly ? 'not-allowed' : 'pointer', opacity: isReadOnly ? 0.6 : 1 }}
                >
                  {badge.label}
                </div>
              ))}
            </div>
            <Form.Text className="text-muted">
              Select badges that will be displayed with your product
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      {/* Additional Product Description */}
      <Row>
        <Col md={12} className="mb-4">
          <Form.Group>
            <Form.Label className="product-detail-form-label">Additional Product Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={data.additionalInfo?.productDesc || ''}
              onChange={(e) => handleInputChange('additionalInfo.productDesc', e.target.value)}
              placeholder="Beginner tennis player who starts discovering tennis on the court! Full synthetic upper featuring Softmove midsole absorbs impact. Designed with characteristc mesh nylon reinforcement. Comes with antibacterial, drex/fabric/oil, softbank/padded/memory and athletic support offering a high level of comfort. From lightweight outsole providing durability, softbank to reinforcing living. The contrasts speed. Suitable for extra comfort, and they combine with their synthetic and textured outsoles..."
              className="product-detail-form-control product-detail-textarea"
              disabled={isReadOnly}
            />
            <Form.Text className="text-muted">
              Detailed product description that will be shown to customers
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default ProductBasicInfo;